import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { checkIdGaps } from "./IdGapHost";
import { toast } from "sonner";
import { Upload, X, FileSpreadsheet, CheckCircle2, AlertCircle, Copy, UserPlus } from "lucide-react";
import { API_BASE } from "./AuthContext";

const SAMPLE_CSV = "title,client,status,priority,due,budget\nTax return 2025 — Acme Coffee,Acme Coffee Roasters,todo,high,2026-09-30,4h\nBAS Q1,BlueWave Logistics,in_progress,medium,2026-04-21,90m";

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const splitLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && (i + 1 < line.length) && line[i + 1] === '"') { cur += '"'; i++; continue; }
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1, 5001).map((l) => {
    const cells = splitLine(l);
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ""; });
    return row;
  });
  return { headers, rows };
}

function autoMap(headers) {
  const map = {};
  const used = new Set();
  const tryAssign = (h, key) => {
    if (used.has(key)) return false;
    map[h] = key; used.add(key); return true;
  };
  for (const h of headers) {
    const lc = h.toLowerCase().trim();
    if (["title", "job", "job title", "engagement"].includes(lc)) { tryAssign(h, "title"); continue; }
    if (["client", "client name", "customer", "company"].includes(lc)) { tryAssign(h, "client_name"); continue; }
    if (["status", "state"].includes(lc)) { tryAssign(h, "status"); continue; }
    if (["priority", "urgency"].includes(lc)) { tryAssign(h, "priority"); continue; }
    if (["due", "due date", "due at", "deadline"].includes(lc)) { tryAssign(h, "due_at"); continue; }
    if (["budget", "budget minutes", "estimated minutes", "hours", "estimated hours"].includes(lc)) { tryAssign(h, "budget_minutes"); continue; }
    if (["description", "notes", "memo"].includes(lc)) { tryAssign(h, "description"); continue; }
    map[h] = "";
  }
  return map;
}

export default function JobImportWizard({ open, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [fieldDefs, setFieldDefs] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [previewResult, setPreviewResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipDupes, setSkipDupes] = useState(true);
  const [createMissingClients, setCreateMissingClients] = useState(true);
  const [commitResult, setCommitResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_BASE}/jobs/import/fields`).then((r) => setFieldDefs(r.data?.fields || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setParsed({ headers: [], rows: [] });
      setColumnMap({}); setPreviewResult(null); setCommitResult(null);
    }
  }, [open]);

  const fieldByKey = useMemo(() => Object.fromEntries(fieldDefs.map((f) => [f.key, f])), [fieldDefs]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) {
      toast.error("Please pick a .csv file");
      return;
    }
    setFile(f);
    const text = await f.text();
    const p = parseCSV(text);
    if (p.headers.length === 0 || p.rows.length === 0) {
      toast.error("CSV looks empty");
      return;
    }
    setParsed(p);
    setColumnMap(autoMap(p.headers));
    setStep(2);
  };

  const onPasteSample = () => {
    const p = parseCSV(SAMPLE_CSV);
    setFile({ name: "sample.csv" });
    setParsed(p);
    setColumnMap(autoMap(p.headers));
    setStep(2);
  };

  const goPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/jobs/import/preview`, {
        rows: parsed.rows, column_map: columnMap,
        create_missing_clients: createMissingClients,
      });
      setPreviewResult(r.data);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const goCommit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/jobs/import/commit`, {
        rows: parsed.rows, column_map: columnMap,
        skip_dupes: skipDupes,
        create_missing_clients: createMissingClients,
      });
      setCommitResult(r.data);
      setStep(4);
      toast.success(`Imported ${r.data.created_count} job(s)`);
      checkIdGaps({ jobIds: (r.data.created || []).map((c) => c.job_id), autoFlag: true, source: `Imported ${r.data.created_count} job(s)` });
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const titleMappedTo = Object.entries(columnMap).find(([, v]) => v === "title");
  const canPreview = !!titleMappedTo;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
      onClick={onClose}
      data-testid="job-import-wizard"
    >
      <div
        className="w-full sm:max-w-3xl max-h-[90vh] overflow-hidden rounded-t-lg sm:rounded-lg shadow-xl flex flex-col"
        style={{ background: "var(--paper)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-[var(--ink-2)]" />
            <div className="font-medium text-[var(--ink)]">Bulk job import — step {step} of 4</div>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="job-import-close">
            <X size={18}/>
          </button>
        </div>

        <div className="px-5 py-2 border-b text-[11px] text-[var(--ink-3)] flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
          {["Upload", "Map columns", "Preview", "Done"].map((label, i) => (
            <span key={label} className={`px-2 py-0.5 rounded ${step === i + 1 ? "bg-[var(--ink)] text-[var(--paper)]" : ""}`}>
              {i + 1}. {label}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-4" data-testid="job-import-step-1">
              <div className="border-2 border-dashed rounded-lg p-8 text-center" style={{ borderColor: "var(--border)" }}>
                <Upload size={32} className="mx-auto text-[var(--ink-3)] mb-3"/>
                <div className="text-[14px] text-[var(--ink-2)] mb-2">Pick a CSV file (max 5000 rows)</div>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    onChange={onPickFile}
                    className="hidden"
                    data-testid="job-import-file-input"
                  />
                  <span
                    className="inline-block px-4 py-2 rounded-md text-[13px] font-medium cursor-pointer"
                    style={{ background: "var(--ink)", color: "var(--paper)" }}
                  >
                    Choose file
                  </span>
                </label>
                <div className="text-[11px] text-[var(--ink-3)] mt-3">
                  or <button type="button" onClick={onPasteSample} className="underline" data-testid="job-import-load-sample">load a 2-row sample</button>
                </div>
              </div>
              <div className="text-[12px] text-[var(--ink-3)]">
                <div className="font-medium text-[var(--ink-2)] mb-1">Recognised headers</div>
                <code className="block text-[11px] bg-[var(--subtle)] rounded px-2 py-1.5 font-mono">title, client, status, priority, due, budget (e.g. "4h" or "90m"), description</code>
                <div className="mt-2">Budget accepts <code>120</code>, <code>120m</code>, <code>2h</code>, or <code>1.5h</code>. Due dates accept ISO 8601 or <code>YYYY-MM-DD</code>.</div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="job-import-step-2">
              <div className="text-[13px] text-[var(--ink-2)]">
                <span className="font-medium">{file?.name}</span> · {parsed.rows.length} row(s) · {parsed.headers.length} column(s).
                Map each column to a target field. <strong>Title</strong> is required.
              </div>
              <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--border)" }}>
                <table className="w-full text-[13px]">
                  <thead className="text-[11px] uppercase tracking-wide" style={{ background: "var(--subtle)", color: "var(--ink-3)" }}>
                    <tr><th className="text-left px-3 py-2">CSV column</th><th className="text-left px-3 py-2">Sample</th><th className="text-left px-3 py-2">→ Target field</th></tr>
                  </thead>
                  <tbody>
                    {parsed.headers.map((h) => (
                      <tr key={h} className="border-t" style={{ borderColor: "var(--border)" }}>
                        <td className="px-3 py-2 font-mono">{h}</td>
                        <td className="px-3 py-2 text-[var(--ink-3)] truncate max-w-[200px]">{parsed.rows[0]?.[h]}</td>
                        <td className="px-3 py-2">
                          <select
                            value={columnMap[h] || ""}
                            onChange={(e) => setColumnMap((m) => ({ ...m, [h]: e.target.value }))}
                            className="px-2 py-1 rounded border bg-transparent text-[12px]"
                            style={{ borderColor: "var(--border)" }}
                            data-testid={`job-import-map-${h.replace(/\s+/g, '-')}`}
                          >
                            <option value="">— Skip —</option>
                            {fieldDefs.map((d) => (<option key={d.key} value={d.key}>{d.label}{d.required ? " *" : ""}</option>))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                <input type="checkbox" checked={createMissingClients} onChange={(e) => setCreateMissingClients(e.target.checked)} data-testid="job-import-create-clients"/>
                Auto-create missing clients (type=business) so referenced clients don't block the import
              </label>
              {!canPreview && (
                <div className="text-[12px] text-[var(--accent)] inline-flex items-center gap-1">
                  <AlertCircle size={12}/> Map at least one column to <strong>Title</strong> to continue.
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setStep(1)} className="px-3 py-2 rounded-md text-[13px] border" style={{ borderColor: "var(--border)" }} data-testid="job-import-back-1">Back</button>
                <button onClick={goPreview} disabled={!canPreview || busy} className="px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-50" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="job-import-preview">
                  {busy ? "Validating…" : "Preview validation →"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && previewResult && (
            <div className="space-y-4" data-testid="job-import-step-3">
              <div className="grid grid-cols-4 gap-3 text-center">
                <Stat label="Rows" value={previewResult.total} />
                <Stat label="Valid" value={previewResult.valid} color="#3a7c3a" />
                <Stat label="Duplicates" value={previewResult.duplicates} color="#B91C1C" />
                <Stat label="Invalid" value={previewResult.invalid} color="#a82a2a" />
              </div>
              {previewResult.new_clients_needed && previewResult.new_clients_needed.length > 0 && (
                <div className="text-[12px] text-[var(--ink-2)] border rounded p-3 inline-flex items-start gap-2" style={{ borderColor: "var(--border)", background: "var(--subtle)" }}>
                  <UserPlus size={14} className="mt-0.5"/>
                  <div>
                    <div className="font-medium">{previewResult.new_clients_needed.length} new client(s) will be created:</div>
                    <div className="text-[var(--ink-3)] mt-1">{previewResult.new_clients_needed.slice(0, 8).join(", ")}{previewResult.new_clients_needed.length > 8 ? "…" : ""}</div>
                  </div>
                </div>
              )}
              <div className="border rounded-md overflow-hidden max-h-72 overflow-y-auto" style={{ borderColor: "var(--border)" }}>
                <table className="w-full text-[12px]">
                  <thead className="text-[10px] uppercase tracking-wide sticky top-0" style={{ background: "var(--subtle)", color: "var(--ink-3)" }}>
                    <tr>
                      <th className="text-left px-2 py-1.5">#</th>
                      <th className="text-left px-2 py-1.5">Title</th>
                      <th className="text-left px-2 py-1.5">Client</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      <th className="text-left px-2 py-1.5">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewResult.issues.slice(0, 200).map((row) => (
                      <tr key={row.row_index} className="border-t" style={{ borderColor: "var(--border)" }}>
                        <td className="px-2 py-1.5 font-mono text-[var(--ink-3)]">{row.row_index + 1}</td>
                        <td className="px-2 py-1.5">{row.title || <em className="text-[var(--ink-3)]">(none)</em>}</td>
                        <td className="px-2 py-1.5">{row.client_name}{row.client_id ? <span className="text-[var(--ink-3)]"> ✓</span> : null}</td>
                        <td className="px-2 py-1.5">{row.status}</td>
                        <td className="px-2 py-1.5">
                          {row.errors.length > 0 ? <span className="text-[#a82a2a]">{row.errors.join(", ")}</span> :
                            row.is_duplicate ? <span className="text-[#B91C1C]">Duplicate</span> :
                            <span className="text-[#3a7c3a]">Will create</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                <input type="checkbox" checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} data-testid="job-import-skip-dupes"/>
                Skip duplicates (same title + client already exists)
              </label>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setStep(2)} className="px-3 py-2 rounded-md text-[13px] border" style={{ borderColor: "var(--border)" }} data-testid="job-import-back-2">Back</button>
                <button onClick={goCommit} disabled={busy || previewResult.valid === 0} className="px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-50" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="job-import-commit">
                  {busy ? "Importing…" : `Import ${previewResult.valid + (skipDupes ? 0 : previewResult.duplicates)} job(s)`}
                </button>
              </div>
            </div>
          )}

          {step === 4 && commitResult && (
            <div className="space-y-4" data-testid="job-import-step-4">
              <div className="text-center py-6">
                <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "#3a7c3a" }}/>
                <div className="text-[18px] font-medium text-[var(--ink)]" data-testid="job-import-result-created">
                  {commitResult.created_count} job(s) imported
                </div>
                <div className="text-[var(--ink-3)] text-[12px] mt-1">
                  {commitResult.created_clients_count > 0 && <span>{commitResult.created_clients_count} new client(s) created · </span>}
                  {commitResult.skipped_count} skipped
                </div>
              </div>
              {commitResult.skipped.length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-[var(--ink-3)] inline-flex items-center gap-1">
                    <Copy size={11}/> Show skipped rows
                  </summary>
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2" style={{ borderColor: "var(--border)" }}>
                    {commitResult.skipped.map((s) => (
                      <div key={`skip-${s.row_index}`} className="text-[11px] text-[var(--ink-3)]">
                        Row {s.row_index + 1}: <strong>{s.reason}</strong>{s.title ? ` (${s.title})` : ""}{s.client_name ? ` · ${s.client_name}` : ""}{s.errors ? ` — ${s.errors.join(", ")}` : ""}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="flex items-center justify-end">
                <button onClick={onClose} className="px-4 py-2 rounded-md text-[13px] font-medium" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="job-import-done">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
      <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)]">{label}</div>
      <div className="text-[24px] font-mono font-medium mt-1" style={{ color: color || "var(--ink)" }}>{value}</div>
    </div>
  );
}
